import { gql } from "@apollo/client"
import { useEffect, useMemo, useState } from "react"
import moment from "moment"
import { useQuery } from "@apollo/client"
import TaskList from "./TaskList"
import "./MyTasksGroupedList.css"

export const AllTasksQuery = gql`query AllTasks($orderBy:[String], $first: Int, $after: String, $searchTerm: String, $incidentId: ID, $filters: [FilterInput]){
    allTasks(orderBy: $orderBy, first: $first, after: $after, searchTerm: $searchTerm, incidentId: $incidentId, filters: $filters){
        edges{
            node{
                id
            }
        }
        pageInfo{
            hasNextPage
            endCursor
            length
        }
    }
    
}`
const MY_TASK_GROUPS = [
    { key: "withoutDate", title: "Without Date", colorClass: "task-group-withoutDate" },
    { key: "overdue", title: "Overdue", colorClass: "task-group-overdue" },
    { key: "today", title: "Today", colorClass: "task-group-today" },
    { key: "thisWeek", title: "This Week", colorClass: "task-group-thisWeek" },
    { key: "nextWeek", title: "Next Week", colorClass: "task-group-nextWeek" },
    { key: "later", title: "Later", colorClass: "task-group-later" },
]

// One generic filter per group, replacing the dueDateGte/Lte/IsNull arguments.
// The old `dueDateIsNull: false` companion is dropped: a SQL comparison against
// NULL is never true, so every range filter already excludes undated tasks.
export function buildGroupDateFilter(groupKey) {
    const today = moment().startOf("day")
    const tomorrow = today.clone().add(1, "day")
    const endOfWeek = today.clone().endOf("isoWeek")
    const startOfNextWeek = today.clone().add(1, "week").startOf("isoWeek")
    const endOfNextWeek = today.clone().add(1, "week").endOf("isoWeek")
    const afterNextWeek = endOfNextWeek.clone().add(1, "day")
    const day = (m) => m.format("YYYY-MM-DD")
    const on = (filterType, filterValues) => [{ fieldName: "due_date", filterType, filterValues }]

    if (groupKey === "withoutDate") return on("is_null", null)
    if (groupKey === "overdue") return on("less_than_or_equal", day(today.clone().subtract(1, "day")))
    if (groupKey === "today") return on("equal", day(today))
    if (groupKey === "thisWeek") return on("between", [day(tomorrow), day(endOfWeek)])
    if (groupKey === "nextWeek") return on("between", [day(startOfNextWeek), day(endOfNextWeek)])
    return on("greater_than_or_equal", day(afterNextWeek))
}

function MyTasksGroupSection({ group, groupIndex, collapsed, onToggle, searchTerm, orderBy, updateOrderBy, columnConfig, onCountReady, filters }) {
    // The group's date range is structure, not a user choice -- composed onto
    // whatever filters the user set, at the leaf, rather than surfaced in the
    // popup or the chips.
    const combinedFilters = [...(filters || []), ...group.filters]
    const { data: groupTasksData, loading: groupTasksLoading } = useQuery(AllTasksQuery, {
        variables: {
            searchTerm,
            orderBy,
            first: 1,
            filters: combinedFilters,
        },
        fetchPolicy: "cache-first",
    })

    const groupCount = groupTasksData?.allTasks?.pageInfo?.length
    const resolvedGroupCount = typeof groupCount === "number" ? groupCount : 0
    const summaryText = resolvedGroupCount === 0 ? "No tasks" : `${resolvedGroupCount} task${resolvedGroupCount === 1 ? "" : "s"}`

    useEffect(() => {
        if (groupTasksLoading || typeof groupCount !== "number") {
            return
        }
        onCountReady(group.key, groupCount)
    }, [group.key, groupCount, groupTasksLoading, onCountReady])

    return (
        <div key={group.key} className={`${group.colorClass} task-group-section ${groupIndex === 0 ? "task-group-first" : ""} ${collapsed ? "task-group-section--collapsed" : "task-group-section--expanded"}`}>
            <button
                type="button"
                className="task-group-toggle"
                onClick={() => onToggle(group.key)}
                aria-expanded={!collapsed}
            >
                <span className="task-group-toggle__left">
                    <span className="task-group-toggle__icon" aria-hidden="true">
                        <i className={`fa ${collapsed ? "fa-chevron-right" : "fa-chevron-down"}`} />
                    </span>
                    <span className="task-group-toggle__content">
                        <span className="task-group-title-row">
                            <span className="task-group-title">{group.title}</span>
                            <span className="task-group-count">{resolvedGroupCount}</span>
                        </span>
                        <span className="task-group-summary">{summaryText}</span>
                    </span>
                </span>
                <span className="task-group-toggle__state">{collapsed ? "Show" : "Hide"}</span>
            </button>
            {!collapsed && (
                <div className="task-group-body">
                    <TaskList
                        searchTerm={searchTerm}
                        orderBy={orderBy}
                        updateOrderBy={updateOrderBy}
                        columnConfig={columnConfig}
                        filters={combinedFilters}
                    />
                </div>
            )}
        </div>
    )
}

// No currentUserId prop here: the for_user filter arrives baked into `filters`
// as a preset (see getTaskFilterPresets), so this component never needs to
// build it, or know who the current user is, itself.
function MyTasksGroupedList({ searchTerm, orderBy, updateOrderBy, columnConfig, filters }) {
    const [collapsedGroups, setCollapsedGroups] = useState({})
    const [groupCounts, setGroupCounts] = useState({})
    const groups = useMemo(() => MY_TASK_GROUPS.map(group => ({ ...group, filters: buildGroupDateFilter(group.key) })), [])

    const toggleGroup = (groupKey) => {
        setCollapsedGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }))
    }

    const handleCountReady = (groupKey, count) => {
        setGroupCounts((prev) => {
            if (prev[groupKey] === count) return prev
            return { ...prev, [groupKey]: count }
        })
        setCollapsedGroups((prev) => {
            if (Object.prototype.hasOwnProperty.call(prev, groupKey)) {
                return prev
            }
            return { ...prev, [groupKey]: count === 0 }
        })
    }

    return (
        <div className="TaskList my-tasks-grouped-list">
            {groups.map((group, groupIndex) => (
                <MyTasksGroupSection
                    key={group.key}
                    group={group}
                    groupIndex={groupIndex}
                    collapsed={Object.prototype.hasOwnProperty.call(collapsedGroups, group.key) ? !!collapsedGroups[group.key] : ((groupCounts[group.key] ?? 0) === 0)}
                    onToggle={toggleGroup}
                    onCountReady={handleCountReady}
                    searchTerm={searchTerm}
                    orderBy={orderBy}
                    updateOrderBy={updateOrderBy}
                    columnConfig={columnConfig}
                    filters={filters}
                />
            ))}
        </div>
    )
}

export default MyTasksGroupedList
